import { slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.esm.js';
import { useLocale } from '@react-aria/i18n';
import { useLayoutEffect, useResizeObserver } from '@react-aria/utils';
import { useState, useRef, useEffect, useCallback } from 'react';
import calculatePosition from './calculate-position.esm.js';
import useCloseOnScroll from './use-close-on-scroll.esm.js';

// @ts-ignore
var visualViewport = typeof document !== 'undefined' && window.visualViewport;

/**
 * Handles positioning overlays like popovers and menus relative to a trigger
 * element, and updating the position when the window resizes.
 */
function useOverlayPosition(props) {
  var _useLocale = useLocale(),
    direction = _useLocale.direction;
  var _props$arrowSize = props.arrowSize,
    arrowSize = _props$arrowSize === void 0 ? 0 : _props$arrowSize,
    targetRef = props.targetRef,
    overlayRef = props.overlayRef,
    _props$scrollRef = props.scrollRef,
    scrollRef = _props$scrollRef === void 0 ? overlayRef : _props$scrollRef,
    _props$placement = props.placement,
    placement = _props$placement === void 0 ? 'bottom' : _props$placement,
    _props$containerPaddi = props.containerPadding,
    containerPadding = _props$containerPaddi === void 0 ? 12 : _props$containerPaddi,
    _props$shouldFlip = props.shouldFlip,
    shouldFlip = _props$shouldFlip === void 0 ? true : _props$shouldFlip,
    _props$boundaryElemen = props.boundaryElement,
    boundaryElement = _props$boundaryElemen === void 0 ? typeof document !== 'undefined' ? document.body : null : _props$boundaryElemen,
    _props$offset = props.offset,
    offset = _props$offset === void 0 ? 0 : _props$offset,
    _props$crossOffset = props.crossOffset,
    crossOffset = _props$crossOffset === void 0 ? 0 : _props$crossOffset,
    _props$shouldUpdatePo = props.shouldUpdatePosition,
    shouldUpdatePosition = _props$shouldUpdatePo === void 0 ? true : _props$shouldUpdatePo,
    _props$isOpen = props.isOpen,
    isOpen = _props$isOpen === void 0 ? true : _props$isOpen,
    onClose = props.onClose,
    maxHeight = props.maxHeight,
    _props$arrowBoundaryO = props.arrowBoundaryOffset,
    arrowBoundaryOffset = _props$arrowBoundaryO === void 0 ? 0 : _props$arrowBoundaryO,
    useOverlayAsContainer = props.useOverlayAsContainer;
  var _useState = useState({
      position: {},
      arrowOffsetLeft: undefined,
      arrowOffsetTop: undefined,
      maxHeight: undefined,
      // @ts-expect-error
      placement: undefined
    }),
    _useState2 = _slicedToArray(_useState, 2),
    position = _useState2[0],
    setPosition = _useState2[1];
  var deps = [shouldUpdatePosition, placement, overlayRef.current, targetRef.current, scrollRef.current, containerPadding, shouldFlip, boundaryElement, offset, crossOffset, isOpen, direction, maxHeight, arrowBoundaryOffset, arrowSize];

  // Note, the position freezing breaks if body sizes itself dynamicly with the visual viewport but that might
  // just be a non-realistic use case
  // Upon opening a overlay, record the current visual viewport scale so we can freeze the overlay styles
  //@ts-expect-error
  var lastScale = useRef(visualViewport === null || visualViewport === void 0 ? void 0 : visualViewport.scale);
  useEffect(function () {
    if (isOpen) {
      //@ts-expect-error
      lastScale.current = visualViewport === null || visualViewport === void 0 ? void 0 : visualViewport.scale;
    }
  }, [isOpen]);
  var updatePosition = useCallback(function () {
    if (shouldUpdatePosition === false || !isOpen || !overlayRef.current || !targetRef.current || !scrollRef.current || !boundaryElement) {
      return;
    }
    //@ts-expect-error
    if ((visualViewport === null || visualViewport === void 0 ? void 0 : visualViewport.scale) !== lastScale.current) {
      return;
    }

    // Always reset the overlay's previous max height if not defined by the user so that we can compensate for
    // RAC collections populating after a second render and properly set a correct max height + positioning when it populates.
    var overlay = overlayRef.current;
    if (!maxHeight && overlayRef.current) {
      var _window$visualViewpor, _window$visualViewpor2;
      overlay.style.top = '0px';
      overlay.style.bottom = '';
      overlay.style.maxHeight = ((_window$visualViewpor = (_window$visualViewpor2 = window.visualViewport) === null || _window$visualViewpor2 === void 0 ? void 0 : _window$visualViewpor2.height) !== null && _window$visualViewpor !== void 0 ? _window$visualViewpor : window.innerHeight) + 'px';
    }
    var position = calculatePosition({
      placement: translateRTL(placement, direction),
      overlayNode: overlayRef.current,
      targetNode: targetRef.current,
      scrollNode: scrollRef.current,
      padding: containerPadding,
      shouldFlip: shouldFlip,
      boundaryElement: boundaryElement,
      offset: offset,
      crossOffset: crossOffset,
      maxHeight: maxHeight,
      arrowSize: arrowSize,
      arrowBoundaryOffset: arrowBoundaryOffset,
      useOverlayAsContainer: useOverlayAsContainer
    });

    // Modify overlay styles directly so positioning happens immediately without the need of a second render
    // This is so we don't have to delay autoFocus scrolling or delay applying preventScroll for popovers
    overlay.style.top = '';
    overlay.style.bottom = '';
    //@ts-expect-error
    Object.keys(position.position).forEach(
    //@ts-expect-error
    function (key) {
      return overlay.style[key] = position.position[key] + 'px';
    });
    //@ts-expect-error
    overlay.style.maxHeight = position.maxHeight != null ? position.maxHeight + 'px' : undefined;

    // Trigger a set state for a second render anyway for arrow positioning
    setPosition(position);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  // Update position when anything changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(updatePosition, deps);

  // Update position on window resize
  useResize(updatePosition);

  // Update position when the overlay changes size (might need to flip).
  useResizeObserver({
    ref: overlayRef,
    onResize: updatePosition
  });

  // Reposition the overlay and do not close on scroll while the visual viewport is resizing.
  // This will ensure that overlays adjust their positioning when the iOS virtual keyboard appears.
  var isResizing = useRef(false);
  useLayoutEffect(function () {
    var timeout;
    var onResize = function onResize() {
      isResizing.current = true;
      clearTimeout(timeout);
      timeout = setTimeout(function () {
        isResizing.current = false;
      }, 500);
      updatePosition();
    };

    // Only reposition the overlay if a scroll event happens immediately as a result of resize (aka the virtual keyboard has appears)
    // We don't want to reposition the overlay if the user has pinch zoomed in and is scrolling the viewport around.
    var onScroll = function onScroll() {
      if (isResizing.current) {
        onResize();
      }
    };

    // @ts-expect-error
    visualViewport === null || visualViewport === void 0 || visualViewport.addEventListener('resize', onResize);
    // @ts-expect-error
    visualViewport === null || visualViewport === void 0 || visualViewport.addEventListener('scroll', onScroll);
    return function () {
      // @ts-expect-error
      visualViewport === null || visualViewport === void 0 || visualViewport.removeEventListener('resize', onResize);
      // @ts-expect-error
      visualViewport === null || visualViewport === void 0 || visualViewport.removeEventListener('scroll', onScroll);
    };
  }, [updatePosition]);
  var close = useCallback(function () {
    if (!isResizing.current) {
      // @ts-expect-error
      onClose();
    }
  }, [onClose, isResizing]);

  // When scrolling a parent scrollable region of the trigger (other than the body),
  // we hide the popover. Otherwise, its position would be incorrect.
  useCloseOnScroll({
    triggerRef: targetRef,
    isOpen: isOpen,
    onClose: onClose && close
  });
  return {
    overlayProps: {
      style: _objectSpread2(_objectSpread2({
        position: 'absolute',
        zIndex: 100000
      }, position.position), {}, {
        maxHeight: position.maxHeight
      })
    },
    placement: position.placement,
    arrowProps: {
      'aria-hidden': 'true',
      role: 'presentation',
      style: {
        left: position.arrowOffsetLeft,
        top: position.arrowOffsetTop
      }
    },
    updatePosition: updatePosition
  };
}
//@ts-expect-error
function useResize(onResize) {
  useLayoutEffect(function () {
    window.addEventListener('resize', onResize, false);
    return function () {
      window.removeEventListener('resize', onResize, false);
    };
  }, [onResize]);
}
//@ts-expect-error
function translateRTL(position, direction) {
  if (direction === 'rtl') {
    return position.replace('start', 'right').replace('end', 'left');
  }
  return position.replace('start', 'left').replace('end', 'right');
}

export { useOverlayPosition as default };
//# sourceMappingURL=use-overlay-position.esm.js.map
